import { useTheme } from '@mui/material/styles';
import AvailabilitySlotValue from '../common/AvailabilitySlotValue';
import TimelineBase from '../common/TimelineBase';

function TimeLineLasertag({
  times,
  slotMode,
  renderAvailableSlots,
  slotWidth = 140,
  showTimeRow,
  showSlotRows,
  hideTimeRow,
  entertainment,
}) {
  const theme = useTheme();
  const isAgeSplit = entertainment?.ltCapacityMode === 'ageSplit';

  const getAgeSplitColors = () => {
    return { s0: 'orange', s1: 'green' };
  };

  const getAgeSplitLabels = time => {
    const sgt = time.slotGroupType;
    if (sgt === 'adults') return { s0: 'E', s1: 'E' };
    if (sgt === 'kids') return { s0: 'K', s1: 'K' };
    return { s0: 'K', s1: 'E' };
  };

  const renderSlotRows = ({ times }) => {
    if (slotMode) {
      return (
        <>
          <div style={{ display: 'flex', overflowX: 'visible', marginBottom: '0px' }}>
            {times.map(time => {
              const hasAgeSplitData =
                isAgeSplit && (time.slotGroupType !== undefined || time.freesKids !== undefined);
              const bgColor = hasAgeSplitData ? getAgeSplitColors().s0 : theme.palette.warning.main;
              const label = hasAgeSplitData ? getAgeSplitLabels(time).s0 : null;
              const freeColor = hasAgeSplitData ? '' : theme.palette.warning.contrastText;
              return (
                <div
                  key={time.i + 's0'}
                  style={{
                    minWidth: `${slotWidth}px`,
                    textAlign: 'left',
                    backgroundColor: bgColor,
                    fontWeight: 'bold',
                    color: freeColor,
                  }}
                >
                  {hasAgeSplitData ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <span
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.45)',
                          color: '#fff',
                          borderRadius: 3,
                          padding: '0 3px',
                          fontSize: '0.75em',
                          fontWeight: 'bold',
                          lineHeight: 1.4,
                        }}
                      >
                        {label}
                      </span>
                      <AvailabilitySlotValue
                        count={time.s0}
                        freeText={`${time.frees0} frei`}
                        isWarning={time.frees0 < 0}
                        overrideBlocked={time.overrideBlockedS0}
                        dense
                      />
                    </span>
                  ) : (
                    <AvailabilitySlotValue
                      count={time.s0}
                      freeText={`${time.frees0} frei`}
                      isWarning={time.frees0 < 0}
                      overrideBlocked={time.overrideBlockedS0}
                      dense
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', overflowX: 'visible', marginBottom: '10px' }}>
            {times.map(time => {
              const hasAgeSplitData =
                isAgeSplit && (time.slotGroupType !== undefined || time.freesKids !== undefined);
              const bgColor = hasAgeSplitData ? getAgeSplitColors().s1 : theme.palette.success.main;
              const label = hasAgeSplitData ? getAgeSplitLabels(time).s1 : null;
              const freeColor = hasAgeSplitData ? '' : theme.palette.success.contrastText;
              return (
                <div
                  key={time.i + 's1'}
                  style={{
                    minWidth: `${slotWidth}px`,
                    textAlign: 'left',
                    backgroundColor: bgColor,
                    fontWeight: 'bold',
                    color: freeColor,
                  }}
                >
                  {hasAgeSplitData ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <span
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.45)',
                          color: '#fff',
                          borderRadius: 3,
                          padding: '0 3px',
                          fontSize: '0.75em',
                          fontWeight: 'bold',
                          lineHeight: 1.4,
                        }}
                      >
                        {label}
                      </span>
                      <AvailabilitySlotValue
                        count={time.s1}
                        freeText={`${time.frees1} frei`}
                        isWarning={time.frees1 < 0}
                        overrideBlocked={time.overrideBlockedS1}
                        dense
                      />
                    </span>
                  ) : (
                    <AvailabilitySlotValue
                      count={time.s1}
                      freeText={`${time.frees1} frei`}
                      isWarning={time.frees1 < 0}
                      overrideBlocked={time.overrideBlockedS1}
                      dense
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      );
    }
    const slotBg =
      theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800];
    return (
      <div style={{ display: 'flex', overflowX: 'visible', marginBottom: '0px' }}>
        {times.map(time => {
          const sgt = time.slotGroupType;
          const showBothBadges =
            isAgeSplit &&
            time.freesKids !== undefined &&
            sgt !== 'adults' &&
            sgt !== 'kids';
          const showSingleBadge =
            isAgeSplit &&
            time.freesKids !== undefined &&
            (sgt === 'adults' || sgt === 'kids');
          const useBadgeLayout = showBothBadges || showSingleBadge;
          return (
            <div
              key={time.i + 's0'}
              style={{
                minWidth: `${slotWidth}px`,
                textAlign: 'left',
                backgroundColor: useBadgeLayout ? 'black' : slotBg,
                fontWeight: 'bold',
                color: useBadgeLayout ? '' : theme.palette.text.primary,
              }}
            >
              {showBothBadges ? (
                <span style={{ fontSize: '0.85em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span
                    style={{
                      backgroundColor: 'orange',
                      color: '#fff',
                      borderRadius: 3,
                      padding: '0 3px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    K{time.freesKids}
                  </span>
                  <span
                    style={{
                      backgroundColor: 'green',
                      color: '#fff',
                      borderRadius: 3,
                      padding: '0 3px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    E{time.freesAdults}
                  </span>
                  <span style={{ fontSize: '0.85em' }}>frei</span>
                </span>
              ) : showSingleBadge ? (
                <span style={{ fontSize: '0.85em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span
                    style={{
                      backgroundColor: sgt === 'kids' ? 'orange' : 'green',
                      color: '#fff',
                      borderRadius: 3,
                      padding: '0 3px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sgt === 'kids' ? 'K' : 'E'}
                    {time.frees0}
                  </span>
                  <span style={{ fontSize: '0.85em' }}>frei</span>
                </span>
              ) : (
                <AvailabilitySlotValue
                  count={time.s0}
                  freeText={`${time.frees0} frei`}
                  isWarning={time.frees0 < 0}
                  overrideBlocked={time.overrideBlocked}
                  dense
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <TimelineBase
      times={times}
      slotWidth={slotWidth}
      renderSlotRows={renderSlotRows}
      renderAvailableSlots={renderAvailableSlots}
      showTimeRow={showTimeRow}
      showSlotRows={showSlotRows}
      hideTimeRow={hideTimeRow}
    />
  );
}

export default TimeLineLasertag;
